import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatCurrency } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Scale, Trophy, Home } from "lucide-react";

const METRICS = [
  { key: "value", label: "Valor actual", type: "money" },
  { key: "equity", label: "Equity (valor − hipoteca)", type: "money" },
  { key: "rental_income", label: "Renta mensual", type: "money" },
  { key: "annual_yield", label: "Yield bruto anual", type: "percent" },
  { key: "net_yield", label: "Yield neto anual", type: "percent" },
  { key: "mortgage_remaining", label: "Hipoteca pendiente", type: "money" },
  { key: "mortgage_paid_pct", label: "% Hipoteca amortizada", type: "percent" },
  { key: "ltv_pct", label: "LTV (hipoteca / valor)", type: "percent" },
];

export default function ComparePropertiesDialog({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]); setResult(null);
    api.get("/analytics/real-estate")
      .then((r) => setItems(r.data?.items || []))
      .catch((e) => logger.error("compare list", e));
  }, [open]);

  const toggle = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[1], id]; // rolling window of 2
      return [...prev, id];
    });
  };

  const canCompare = selected.length === 2;

  const runCompare = async () => {
    if (!canCompare) return;
    setLoading(true);
    try {
      const r = await api.get(`/analytics/real-estate/compare?ids=${selected.join(",")}`);
      setResult(r.data);
    } catch (e) {
      logger.error("compare fetch", e);
    } finally { setLoading(false); }
  };

  const fmt = (v, type, currency) => {
    if (v === null || v === undefined) return "—";
    if (type === "money") return formatCurrency(v, currency);
    if (type === "percent") return `${Number(v).toFixed(2)}%`;
    return String(v);
  };

  const winnerIcon = (metricKey, itemId) =>
    result?.winners?.[metricKey] === itemId ? (
      <Trophy className="w-3.5 h-3.5 text-[#C89A3F] inline-block ml-1" />
    ) : null;

  const winnersCount = useMemo(() => {
    if (!result) return {};
    const counts = {};
    Object.values(result.winners || {}).forEach((id) => {
      counts[id] = (counts[id] || 0) + 1;
    });
    return counts;
  }, [result]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl bg-white max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Scale className="w-5 h-5 text-[#1C7C54]" />
            Comparar propiedades
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="flex flex-col gap-4 overflow-auto">
            <p className="text-sm text-[#5F7368]">
              Selecciona 2 propiedades para ver una comparativa lado a lado y descubrir cuál rinde más.
            </p>
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#8A9992]">
                No hay propiedades para comparar.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {items.map((it) => {
                  const isSel = selected.includes(it.id);
                  return (
                    <button
                      key={it.id}
                      type="button"
                      data-testid={`compare-pick-${it.id}`}
                      onClick={() => toggle(it.id)}
                      className={`text-left rounded-xl border px-4 py-3 transition-all ${
                        isSel
                          ? "border-[#1C7C54] bg-[#1C7C54]/5 ring-2 ring-[#1C7C54]/30"
                          : "border-[#EBEBEA] hover:border-[#1C7C54]/40"
                      }`}
                    >
                      <div className="flex items-center gap-2 font-medium text-[#0A110E]">
                        <Home className="w-4 h-4 text-[#1C7C54]" />
                        {it.name}
                      </div>
                      {it.address && (
                        <div className="text-xs text-[#8A9992] truncate mt-0.5">{it.address}</div>
                      )}
                      <div className="text-xs text-[#5F7368] mt-1 tabular-nums">
                        {formatCurrency(it.value, it.currency)}
                        {it.annual_yield ? ` · ${Number(it.annual_yield).toFixed(2)}% bruto` : ""}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button
                data-testid="compare-run-btn"
                onClick={runCompare}
                disabled={!canCompare || loading}
                className="bg-[#0F291E] hover:bg-[#1C7C54]"
              >
                {loading ? "Comparando…" : `Comparar (${selected.length}/2)`}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 overflow-auto" data-testid="compare-result">
            <div className="grid grid-cols-2 gap-3">
              {result.items.map((it) => {
                const totalMetrics = METRICS.length;
                const wins = winnersCount[it.id] || 0;
                return (
                  <div key={it.id} className="rounded-xl border border-[#EBEBEA] p-4">
                    <div className="flex items-center gap-2 font-medium text-[#0A110E]">
                      <Home className="w-4 h-4 text-[#1C7C54]" />
                      {it.name}
                    </div>
                    {it.address && (
                      <div className="text-xs text-[#8A9992] truncate">{it.address}</div>
                    )}
                    <div className="mt-2 text-xs">
                      <span className="rounded-full bg-[#C89A3F]/10 text-[#8B6B1F] px-2 py-0.5 font-semibold tabular-nums">
                        <Trophy className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                        {wins}/{totalMetrics} victorias
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border border-[#EBEBEA] rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAF9]">
                  <tr className="text-xs uppercase tracking-[0.08em] text-[#8A9992]">
                    <th className="text-left px-4 py-2">Métrica</th>
                    {result.items.map((it) => (
                      <th key={it.id} className="text-right px-4 py-2 truncate max-w-[40%]">{it.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {METRICS.map((m) => (
                    <tr key={m.key} className="border-t border-[#EBEBEA]">
                      <td className="px-4 py-2 text-[#5F7368]">{m.label}</td>
                      {result.items.map((it) => {
                        const isWinner = result.winners?.[m.key] === it.id;
                        return (
                          <td
                            key={it.id}
                            data-testid={`compare-cell-${m.key}-${it.id}`}
                            className={`px-4 py-2 text-right tabular-nums ${
                              isWinner ? "font-semibold text-[#1C7C54]" : "text-[#0A110E]"
                            }`}
                          >
                            {fmt(it[m.key], m.type, it.currency)}
                            {winnerIcon(m.key, it.id)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResult(null)} data-testid="compare-back-btn">
                Elegir otras
              </Button>
              <Button className="bg-[#0F291E] hover:bg-[#1C7C54]" onClick={() => onOpenChange(false)}>
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
